import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  TablePagination,
  Stack,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2C5F8E",
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#EBF2FF",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

// Mock Data
const statData = [
  { title: "Total Users", value: 1200 },
  { title: "Active Users", value: 950 },
  { title: "Modules Running", value: 7 },
  { title: "System Alerts", value: 4 },
];

const systemStats = [
  { module: "Authentication", requests: 3000, errors: 12, uptime: "99.9%" },
  { module: "Data Processing", requests: 2800, errors: 5, uptime: "99.7%" },
  { module: "Reporting", requests: 2100, errors: 3, uptime: "99.8%" },
  { module: "Analytics", requests: 2200, errors: 4, uptime: "99.6%" },
  { module: "Notifications", requests: 1800, errors: 6, uptime: "99.5%" },
  { module: "Storage", requests: 1700, errors: 2, uptime: "99.9%" },
];

const usageOverTime = [
  { month: "Jan", requests: 10000 },
  { month: "Feb", requests: 12000 },
  { month: "Mar", requests: 15000 },
  { month: "Apr", requests: 14000 },
  { month: "May", requests: 16000 },
  { month: "Jun", requests: 15000 },
  { month: "Jul", requests: 17500 },
  { month: "Aug", requests: 16500 },
  { month: "Sep", requests: 16000 },
  { month: "Oct", requests: 15500 },
  { month: "Nov", requests: 14500 },
  { month: "Dec", requests: 13500 },
];

// Stat Card Component
const StatCard = ({ title, value }) => (
  <Card
    sx={{
      minHeight: 100,
      textAlign: "center",
      boxShadow: "none",
      border: "2px solid lightgrey",
      borderRadius: 2,
    }}
  >
    <CardContent>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);

// Table Component with Pagination Support
const SystemPerformanceTable = ({ data, page, rowsPerPage }) => {
  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <TableContainer
      sx={{
        border: "1px solid transparent",
        borderRadius: "10px",
        maxWidth: "100vw",
        overflowX: "auto",
        margin: "auto",
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell>#</StyledTableCell>
            <StyledTableCell>Module</StyledTableCell>
            <StyledTableCell>Requests</StyledTableCell>
            <StyledTableCell>Errors</StyledTableCell>
            <StyledTableCell>Uptime</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((module, idx) => (
            <StyledTableRow key={idx}>
              <StyledTableCell>{page * rowsPerPage + idx + 1}</StyledTableCell>
              <StyledTableCell>{module.module}</StyledTableCell>
              <StyledTableCell>{module.requests}</StyledTableCell>
              <StyledTableCell>{module.errors}</StyledTableCell>
              <StyledTableCell>{module.uptime}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Bar Chart Component
const SystemChart = ({ data }) => (
  <Paper sx={{ mt: 4, p: 2, boxShadow:"none", border:'2px solid lightgrey', borderRadius:2 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="h6">System Usage Over Time</Typography>
    </Stack>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="requests" fill="#006CB8" />
      </BarChart>
    </ResponsiveContainer>
  </Paper>
);

// Main View Component
const SystemReportView = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
      >
        System Reports
      </Typography>

      <Grid container spacing={2}>
        {statData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <StatCard title={item.title} value={item.value} />
          </Grid>
        ))}
      </Grid>
      <SystemChart data={usageOverTime} />
      <Box sx={{ mt: 4 }} id="system-chart-table">
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          mb={1}
        >
          System Module Performance
        </Typography>
        <SystemPerformanceTable
          data={systemStats}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default SystemReportView;
